/**
 * Street, postcode and city taken from a Web Research result.
 * Twin: functions/helpers/researchLocation.js — keep the two in sync.
 * The apply step writes a column only when that column is still empty.
 */

const EMPTY = { street: null, postalCode: null, city: null };

export function isBlankContactField(value) {
  if (value == null) return true;
  const s = String(value).trim().toLowerCase();
  return !s || s === '-' || s === 'null' || s === 'none' || s === 'n/a' || s === 'undefined' || s === 'unknown';
}

function clean(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
}

function cleanCity(raw) {
  const city = clean(raw)
    .replace(/[.,;]+$/g, '')
    .replace(/\s+(deutschland|germany|österreich|austria|schweiz|switzerland|europe|europa)$/i, '')
    .trim();
  return city || null;
}

function isPlausibleCity(raw) {
  const city = cleanCity(raw);
  if (!city || city.length < 2 || city.length > 40) return false;
  if (/\d/.test(city)) return false;
  if (!/^[\p{Lu}]/u.test(city)) return false;
  if (/^(deutschland|germany|österreich|austria|schweiz|switzerland|europe|europa|remote|worldwide|global|dach|nrw|bayern|baden-württemberg|baden-wuerttemberg|niedersachsen|hessen|sachsen|sachsen-anhalt|thüringen|thueringen|brandenburg|saarland|rheinland-pfalz|schleswig-holstein|mecklenburg-vorpommern|nordrhein-westfalen|süddeutschland|sueddeutschland|norddeutschland|ostdeutschland|westdeutschland)$/i.test(city)) return false;
  if (!/^[\p{L}.\-' ]+$/u.test(city)) return false;
  if (city.split(/\s+/).length > 4) return false;
  if (city.split(/\s+/).some((word) => /^(tel|telefon|phone|fax|mail|email|e-mail|www|http|mobil|handy|gmbh|ag)$/i.test(word))) return false;
  return true;
}

function cleanStreet(raw) {
  const street = clean(raw).replace(/,\s*$/, '').trim();
  if (!street) return null;
  if (/^(hr[ab]|fn|amtsgericht|registergericht|handelsgericht|ust-?idnr?|vat|iban)\b/i.test(street)) return null;
  return street;
}

export function splitPostalAddress(raw) {
  const prefixed = clean(raw).replace(/,\s*/g, ', ').replace(/^(?:DE|D|AT|A|CH)[-\s]+/i, '');
  if (!prefixed) return { ...EMPTY };

  const reversed = prefixed.match(/^(\d{4,5})\s+([^,]+),\s+(.+)$/);
  if (reversed && isPlausibleCity(reversed[2])) {
    const street = cleanStreet(reversed[3]);
    const city = cleanCity(reversed[2]);
    if (street && city && (/\d/.test(street) || street.split(/\s+/).length >= 2)) {
      return { street, postalCode: reversed[1], city };
    }
  }

  const forward = prefixed.match(/^(?:(.+?)(?:,\s+|\s+))?(?:(?:DE|D|AT|A|CH)[-\s]+)?(\d{4,5})\s+(.+)$/i);
  if (forward && isPlausibleCity(forward[3].split(',')[0])) {
    const rawStreet = clean(forward[1] || '');
    const street = cleanStreet(rawStreet);
    if (/^(hr[ab]|fn|amtsgericht|registergericht|handelsgericht|ust-?idnr?|vat|iban)\b/i.test(rawStreet)) {
      return { ...EMPTY };
    }
    if (street && !/\d/.test(street) && street.split(/\s+/).length < 2) return { ...EMPTY };
    return { street, postalCode: forward[2], city: cleanCity(forward[3].split(',')[0]) };
  }
  return { ...EMPTY };
}

function asCityOnly(raw) {
  const city = cleanCity(raw);
  if (!city || /[,;/|&]|\bund\b|\band\b/i.test(city)) return null;
  if (city.split(/\s+/).length > 3) return null;
  return isPlausibleCity(city) ? city : null;
}

function asStreetLine(raw) {
  const street = cleanStreet(raw);
  if (!street || street.length > 80) return null;
  if (street.split(/\s+/).length > 8) return null;
  if (/[.!?]$/.test(street) || /[!?]/.test(street)) return null;
  if (!/\d+[a-zA-Z]?$/.test(street) && !/^\d+[a-zA-Z]?\s+\S/.test(street)) return null;
  return street;
}

function firstText(...vals) {
  for (const value of vals) {
    const text = clean(value);
    if (text && !/^(null|unknown|undefined|n\/a|none|-)$/i.test(text)) return text;
  }
  return null;
}

function normalizePostal(value) {
  const text = clean(value).replace(/^(?:DE|D|AT|A|CH)[-\s]+/i, '');
  const match = text.match(/^(\d{4}\s?[A-Z]{2}|\d{4,5})$/i);
  return match ? match[1].replace(/\s+/, '').toUpperCase() : null;
}

function absorb(raw, into) {
  const text = clean(raw);
  if (!text) return;
  const parsed = splitPostalAddress(text);
  if (!into.street && parsed.street) into.street = parsed.street;
  if (!into.postalCode && parsed.postalCode) into.postalCode = parsed.postalCode;
  if (!into.city && parsed.city) into.city = parsed.city;
  if (!into.street && !parsed.postalCode) {
    const line = asStreetLine(text);
    if (line) into.street = line;
  }
  if (!into.city && !parsed.postalCode) {
    const city = asCityOnly(text);
    if (city) into.city = city;
  }
}

export function extractResearchLocation(intelligenceData) {
  const intel = intelligenceData || {};
  const crawl = intel.websiteCrawl || {};
  const imprint = crawl.imprint || intel.grokEnrichment?.verifiedWebsiteFacts?.imprint || {};
  const jsonLd = crawl.jsonLd || {};
  const loc = crawl.location || {};
  const into = { street: null, postalCode: null, city: null };

  const street = firstText(loc.street, imprint.street, jsonLd.street);
  if (street && street.length <= 80) into.street = street;
  into.postalCode = normalizePostal(firstText(loc.postalCode, imprint.postalCode, jsonLd.postalCode));
  const city = firstText(loc.city, imprint.city, jsonLd.city);
  if (city && isPlausibleCity(city)) into.city = cleanCity(city);

  absorb(imprint.address, into);
  absorb(loc.address, into);
  absorb(jsonLd.address, into);

  const extras = [];
  const pushAll = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const item of arr) if (typeof item === 'string') extras.push(item);
  };
  pushAll(intel.deepCrawlData?.addresses);
  pushAll(intel.openAIAnalysis?.deepCrawlData?.addresses);
  pushAll(intel.aiContentAnalysis?.contactSummary?.addresses);
  pushAll(intel.scrapingResults?.data?.contactInfo?.addresses);
  if (typeof intel.contactInfo?.address === 'string') extras.push(intel.contactInfo.address);
  for (const line of extras) absorb(line, into);

  absorb(intel.companyProfile?.headquarters, into);
  if (!into.city) {
    const cityOnly = asCityOnly(intel.grokEnrichment?.quickStats?.location);
    if (cityOnly) into.city = cityOnly;
  }
  return into;
}

export function addressUpdatesForContact(intelligenceData, contactData) {
  const found = extractResearchLocation(intelligenceData);
  const contact = contactData || {};
  const out = {};
  if (found.street && isBlankContactField(contact.lead_address)) out.lead_address = found.street;
  if (found.postalCode && isBlankContactField(contact.lead_postcode)) out.lead_postcode = found.postalCode;
  if (found.city && isBlankContactField(contact.lead_city)) out.lead_city = found.city;
  return out;
}
